// convex/lib/config/domains/subscriptions.ts

import { validatorSubscriptionPlan } from "../../../system/plans";
import { validatorCompanySubscription } from "../../../companies/subscriptions";
import { validatorCompany } from "../../../companies";
import { validatorUsers } from "../../../system/users";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// SUBSCRIPTIONS DOMAIN - Status Values for i18n
// ============================================================================

export const SUBSCRIPTION_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "canceled",
  "expired",
] as const;
export const PLAN_STATUSES = ["active", "inactive", "archived"] as const;

export const subscriptionsDomain: DomainGroup = {
  id: "subscriptions",
  // Plumbing del core (motor de límites). Se queda en TODAS las ediciones.
  // En on-prem se desactiva solo la parte de billing de Dodo; los límites de
  // asientos se alimentan desde la licencia firmada.
  name: "Suscripciones",
  description: "Gestión de planes y suscripciones",
  icon: "CreditCard",
  order: 22,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "subscriptions",
    keys: {
      title: "subscriptions.title",
      description: "subscriptions.description",
    },
  },

  modules: [
    {
      id: "subscription_plans",
      name: "Planes",
      description: "Gestión de planes de suscripción",
      icon: "CreditCard",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/super/plans"],
      navigation: {
        showInNav: true,
        contexts: ["super"],
        navOrder: 5,
        status: "active",
      },

      i18n: {
        namespace: "subscriptions.plans",
        keys: {
          title: "subscriptions.plans.title",
          description: "subscriptions.plans.description",
          longDescription: "subscriptions.plans.longDescription",
          searchPlaceholder: "subscriptions.plans.searchPlaceholder",
          emptyState: "subscriptions.plans.emptyState",
          actions: {
            create: "subscriptions.plans.actions.create",
            edit: "subscriptions.plans.actions.edit",
            delete: "subscriptions.plans.actions.delete",
            view: "subscriptions.plans.actions.view",
          },
        },
        translatableFields: ["name", "description"],
        translatableEnums: [{ field: "status", values: PLAN_STATUSES }],
      },

      tables: [
        {
          tableName: "subscription_plans",
          schema: validatorSubscriptionPlan,
          scope: "company" as TableScope,
          relations: [
            {
              key: "companyId",
              sourceField: "companyId",
              schema: validatorCompany,
              tableName: "companies",
            },
          ],
          searchFields: ["name", "description"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "company_subscriptions",
      name: "Suscripción",
      description: "Gestión de suscripción y planes",
      icon: "Calendar",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/subscription", "/super/subscriptions"],
      navigation: {
        showInNav: true,
        contexts: ["settings", "super"],
        navOrder: 14,
        status: "active",
      },

      i18n: {
        namespace: "subscriptions.company",
        keys: {
          title: "subscriptions.company.title",
          description: "subscriptions.company.description",
          longDescription: "subscriptions.company.longDescription",
          searchPlaceholder: "subscriptions.company.searchPlaceholder",
          emptyState: "subscriptions.company.emptyState",
          actions: {
            edit: "subscriptions.company.actions.edit",
            view: "subscriptions.company.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [{ field: "status", values: SUBSCRIPTION_STATUSES }],
      },

      tables: [
        {
          tableName: "company_subscriptions",
          schema: validatorCompanySubscription,
          scope: "company" as TableScope,
          relations: [
            {
              key: "company",
              sourceField: "companyId",
              schema: validatorCompany,
              tableName: "companies",
            },
            {
              key: "plan",
              sourceField: "planId",
              schema: validatorSubscriptionPlan,
              tableName: "subscription_plans",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["status"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [
        { tableName: "companies" },
        { tableName: "subscription_plans" },
        { tableName: "branches" },
      ],
    },
  ],
};
